import { ResponseCode } from '@/common/request/types';
import { useAppRouter } from '@/common/route/useAppRouter';
import { validateDateAfter } from '@/common/validator/validatorHelpers';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_HOMEPAGE } from '@/modules/main/router';
import { piggybankService } from '@/services/piggybank';
import { useVuelidate } from '@vuelidate/core';
import { integer, minValue, required } from '@vuelidate/validators';
import { reactive } from 'vue';

export function usePiggybankBoost() {
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const model = reactive({
        ratio: 0,
        status: null,
        endTime: null,
        startTime: null,
        image: null,
        imageStatus: 0,
        imageFile: null,
        thumbnail: null,
        thumbnailStatus: 0,
        thumbnailFile: null,
        canDelete: false,
        canDisable: false,
        canEdit: true,
        canEnable: false
    });

    const validations = {
        startTime: { required },
        endTime: { required, validateDate: validateDateAfter('startTime') },
        ratio: { required, integer, min: minValue(101) },
        imageFile: {},
        thumbnailFile: {}
    };

    const v$ = useVuelidate(validations, model);

    const fetch = crudAction.executeAction({
        request: piggybankService.boost.get,
        onSuccess: ({ data }) => {
            data.startTime = new Date(data.startTime);
            data.endTime = new Date(data.endTime);
            Object.assign(model, data);
        },
        onError: ({ code }) => {
            if (code === ResponseCode.NothingFound) {
                model.canEdit = true;
                model.status = null;
                model.ratio = 0;
                model.image = null;
                model.imageStatus = 0;
                model.imageFile = null;
                model.thumbnail = null;
                model.thumbnailStatus = 0;
                model.thumbnailFile = null;
                model.endTime = null;
                model.startTime = null;
                model.canDelete = false;
                model.canDisable = false;
                model.canEdit = true;
                model.canEnable = false;
            } else {
                router.push({ name: ROUTE_NAME_HOMEPAGE });
            }
        }
    });

    const actions = {
        update: crudAction.excuteAndNotify({
            validator: v$,
            request: () => {
                if (model.canEdit)
                    return piggybankService.boost.update(
                        model.startTime.getTime(),
                        model.endTime.getTime(),
                        model.ratio,
                        model.imageFile,
                        model.thumbnailFile
                    );

                return piggybankService.boost.updateImage(model.imageFile, model.thumbnailFile);
            },
            onSuccess: fetch
        }),
        remove: crudAction.confirmAndRemove({
            request: piggybankService.boost.remove,
            onSuccess: fetch,
            onFinally: () => v$.value.$reset()
        }),
        disable: crudAction.confirmAndDisable({
            request: piggybankService.boost.disable,
            onSuccess: fetch
        }),
        enable: crudAction.confirmAndEnable({
            request: piggybankService.boost.enable,
            onSuccess: fetch
        })
    };

    return { model, v$, fetch, actions };
}
